import { useCallback, useState } from "react";
import upload from "app/utils/uploader";

/**
 * The stored flyer is an S3 URL; the uploader shows a file name. Falls back to
 * a generic name so a hand-built or legacy URL still reads as an attachment.
 */
export const flyerFileNameFromUrl = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }

  try {
    const { pathname } = new URL(url);
    return decodeURIComponent(pathname.split("/").pop() || "flyer.pdf");
  } catch {
    return "flyer.pdf";
  }
};

export interface FlyerAttachment {
  fileName: string | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  /** Re-seeds from a URL once the program it belongs to has loaded. */
  reset: (url?: string | null) => void;
  /**
   * Uploads a newly picked file and returns what `flyer_pdf_url` should be:
   * a URL, `null` to clear it, or `undefined` when the coach didn't touch the
   * flyer — the caller leaves the key out of the PATCH so it stays as it is.
   */
  resolveUrl: () => Promise<string | null | undefined>;
}

/**
 * CN-2693: flyer state for the program edit forms. The file is only sent to S3
 * on submit, so picking one and then cancelling costs nothing.
 */
export default function useFlyerAttachment(
  initialUrl?: string | null
): FlyerAttachment {
  const [fileName, setFileName] = useState<string | null>(() =>
    flyerFileNameFromUrl(initialUrl)
  );
  const [file, setFile] = useState<File | null>(null);
  const [cleared, setCleared] = useState(false);

  const onFileSelect = useCallback((selected: File) => {
    setFile(selected);
    setFileName(selected.name);
    setCleared(false);
  }, []);

  const onFileRemove = useCallback(() => {
    setFile(null);
    setFileName(null);
    setCleared(true);
  }, []);

  const reset = useCallback((url?: string | null) => {
    setFile(null);
    setCleared(false);
    setFileName(flyerFileNameFromUrl(url));
  }, []);

  const resolveUrl = useCallback(async () => {
    if (file) {
      // `.promise` rather than awaiting the uploader itself: its `then` drops
      // the rejection handler, so a failed upload would never settle.
      const { url } = await upload(file, file.name, file.type, undefined)
        .promise;
      return url as string;
    }

    return cleared ? null : undefined;
  }, [file, cleared]);

  return { fileName, onFileSelect, onFileRemove, reset, resolveUrl };
}
